import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Capacitor } from "@capacitor/core";
import { resumePendingCheckout } from "@/lib/checkout";

// Applies a push-notification deep link once the app is READY (React Router mounted +
// auth resolved). On a native cold-start the notification tap can fire before React
// boots, so nativePush stashes the link in window.__yalolaPendingDeepLink /
// sessionStorage; we consume it here. Warm-start taps come through the
// "yalola:navigate" event. This replaces the old history.pushState() hack, which was
// unreliable on cold start and got overridden by the initial auth redirect — the
// reason a live notification used to drop the user on the feed instead of the live.
export const DeepLinkHandler = () => {
  const navigate = useNavigate();
  const navRef = useRef(navigate);
  navRef.current = navigate;
  const { loading, user } = useApp();
  const userId = user?.id || null;

  useEffect(() => {
    if (loading) return; // wait until auth + router are ready
    // (re-runs when the member signs in: a destination stashed by RequireAuth / the guest gate is applied then)

    const consume = (link) => {
      if (!link || typeof link !== "string") return;
      try {
        window.__yalolaPendingDeepLink = null;
        sessionStorage.removeItem("yalola.pendingDeepLink");
      } catch { /* noop */ }
      const current = window.location.pathname + window.location.search;
      if (link !== current) navRef.current(link);
    };

    // 1) A link captured before this handler mounted (cold start) or stashed while signed out.
    //    Applied ONLY for a signed-in member: a visitor keeps browsing (shared post → sign-in screen)
    //    and lands on the stashed destination right after signing in / registering.
    let pending = null;
    try { pending = window.__yalolaPendingDeepLink || sessionStorage.getItem("yalola.pendingDeepLink"); } catch { /* noop */ }
    // App mobile relancée à froid pendant un paiement Stripe (aucun lien de retour reçu) : on rouvre
    // l'écran de résultat du Checkout en attente, qui relit l'état réel (payé / annulé) chez Stripe.
    if (!pending && userId && Capacitor.isNativePlatform()) pending = resumePendingCheckout();
    if (pending && userId) setTimeout(() => consume(pending), 350);

    // 2) Links dispatched while the app is already running (warm start).
    const onNav = (e) => consume(e?.detail);
    window.addEventListener("yalola:navigate", onNav);
    return () => window.removeEventListener("yalola:navigate", onNav);
  }, [loading, userId]);

  return null;
};

export default DeepLinkHandler;
